import React, {useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import _ from 'underscore';
import {
	AutoScrollActivator,
	closestCenter,
	DndContext,
	type DragCancelEvent,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	KeyboardSensor,
	MeasuringStrategy,
	PointerSensor,
	TouchSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {type RowData} from '@tanstack/react-table';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {styled, useTheme} from '@netx/core-components/Theme';
import {getScrollparent} from '@netx/core-next/utils/element';
import {type RenderRowProps, TableBody, type TableBodyProps} from './TableBody';
import {TableBodyRowDraggable} from './TableBodyRowDraggable';

export const TableBodyRowDraggableStyled = styled(TableBodyRowDraggable)(
	({theme}) => ({
		height: 56,
		position: 'absolute',
		willChange: 'top',
	}),
) as typeof TableBodyRowDraggable;

type RenderRowPropsWithDrag<TData extends RowData> = RenderRowProps<TData> & {
	disableDrag?: boolean;
};

export interface TableBodyDraggableProps<TData extends RowData>
	extends Omit<TableBodyProps<TData>, 'renderRow'> {
	/**
	 * If `true`, dragging will be disabled
	 * @default false
	 */
	disableDrag?: boolean;
	/**
	 * Allows for passing in a custom row renderer.
	 * @param props The props that would have been passed to `TableBodyRowDraggable`
	 * @returns JSX.Element
	 */
	renderRow?: (props: RenderRowPropsWithDrag<TData>) => React.ReactNode;
	/**
	 * Callback for when a row initializes dragging.
	 *
	 * Note: this will have no effect if `disableDrag` is set to `true`
	 *
	 * @param index The index of the row that is being dragged
	 */
	onRowDragStart?: (index: number) => void;
	/**
	 * Callback for when a row finishes dragging.
	 * @param activeRowIndex The index of the row that was dragged
	 * @param overRowIndex The index of the row that the dragged row was dropped over
	 */
	onRowDragEnd?: (activeRowIndex: number, overRowIndex: number) => void;
}

// default row renderer
const rowRenderer = <TData extends RowData>({
	style,
	start,
	...props
}: RenderRowPropsWithDrag<TData>) => (
	<TableBodyRowDraggableStyled<TData>
		{...props}
		style={{
			top: start,
			...style,
		}}
	/>
);

export const TableBodyDraggablePrimitive = <TData extends RowData>(
	{
		arrowFocusTrapRef: _arrowFocusTrapRef,
		disableDrag,
		engagedRowId,
		onRowDragStart,
		onRowDragEnd,
		...props
	}: TableBodyDraggableProps<TData>,
	ref: React.ForwardedRef<HTMLTableSectionElement>,
) => {
	// we need to use a shared ref to support both the outside and the focus trap hook
	const rootRef = useSharedRef(null, [ref]);
	const theme = useTheme();

	// we need to use a shared ref to support both the outside and inside our component
	const arrowFocusTrapRef = useSharedRef(null, [_arrowFocusTrapRef]);

	// track the currently dragging row id
	const [draggingRowId, setDraggingRowId] = useState<string | null>(null);

	// track scroll parent as we will be using this in several places
	const scrollParentRef = useRef<Element | null>(null);
	useEffect(() => {
		if (scrollParentRef.current) return;
		scrollParentRef.current = getScrollparent(rootRef.current!);
	});

	// setup dnd-kit sensors
	// there is something up when using the keyboard sensor where we get stuck and cant move up or down
	// this is related to virtualization BUT not sure why keyboard initiator would ultimately behave any
	// different than pointer initiator in terms of the drag overlay and being able to move up and down
	// to solve for this, we will force the actively dragging row to stay in the dom during a keyboard drag
	// TODO: investigate why this is happening and see if we can fix it or what library is at fault
	const [keyboardSensorActive, setKeyboardSensorActive] = useState(false);
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
			onActivation: ({event}) => {
				setKeyboardSensorActive(true);
			},
		}),
		useSensor(TouchSensor),
	);

	// dnd-kit requires an array of the unique ids of the items for dragging
	const dragItems = useMemo(
		() => props.rows.map((row) => row.id.toString()),
		[props.rows],
	);

	// cache the scroll left for drag overlay
	const dragOverlayLeftMargin = useMemo(
		() =>
			draggingRowId == null ? 0 : scrollParentRef.current?.scrollLeft ?? 0,
		[draggingRowId],
	);

	// drag end handler
	const handleDragEnd = useConstCallback((event: DragEndEvent) => {
		// reset dragging row id
		setDraggingRowId(null);
		// no longer using keyboard sensor
		setKeyboardSensorActive(false);

		// get the row ids for the active and over rows
		// would be really nice if they made the events generic so we could type these
		const rowId = event.active.id as string;
		const toRowId = event.over?.id as undefined | string;
		const fromIndex = props.rows.findIndex((row) => row.id === rowId);
		const toIndex = props.rows.findIndex((row) => row.id === toRowId);

		onRowDragEnd?.(fromIndex, toIndex);

		arrowFocusTrapRef.current?.setUserDisabled(false);
		arrowFocusTrapRef.current?.setActiveItemIndex(toIndex);
	});

	// drag cancel handler
	const handleDragCancel = useConstCallback((event: DragCancelEvent) => {
		setKeyboardSensorActive(false);
		arrowFocusTrapRef.current?.setUserDisabled(false);
	});

	// drag start handler
	const handleDragStart = useConstCallback((event: DragStartEvent) => {
		const rowId = event.active.id as string;
		setDraggingRowId(rowId);
		const rowIndex = props.rows.findIndex((row) => row.id === rowId);

		arrowFocusTrapRef.current?.setActiveItemIndex(rowIndex);
		arrowFocusTrapRef.current?.setUserDisabled(true);

		// TODO: can we find a way to make the drag overlay maintain the x scroll?
		// for now, when you start dragging we will just rest x scroll to 0
		scrollParentRef.current?.scrollTo({
			left: 0,
		});

		onRowDragStart?.(rowIndex);
	});

	// get the required props for the actively dragging row
	const dragData = useMemo(() => {
		if (draggingRowId == null) return {index: -1, row: null};
		const index = props.rows.findIndex(
			(row) => row.id.toString() === draggingRowId,
		);
		return {
			index,
			row: props.rows[index],
		};
	}, [draggingRowId]);

	return (
		<DndContext
			id="table-dnd-context"
			sensors={sensors}
			autoScroll={{
				activator: AutoScrollActivator.Pointer,
				enabled: true,
				threshold: {
					// prevent x auto scroll while dragging
					x: 0,
					// default y threshold, must be provided when overidding the defaults
					y: 0.2,
				},
				// only allow our scroll parent to scroll
				canScroll: (element) => scrollParentRef.current === element,
			}}
			measuring={{
				droppable: {
					strategy: MeasuringStrategy.WhileDragging,
				},
			}}
			modifiers={[restrictToVerticalAxis]}
			collisionDetection={closestCenter}
			onDragCancel={handleDragCancel}
			onDragEnd={handleDragEnd}
			onDragStart={handleDragStart}
		>
			<SortableContext
				id="sortable-table-context"
				items={dragItems}
				disabled={disableDrag || engagedRowId != null}
				strategy={verticalListSortingStrategy}
			>
				<TableBody<TData>
					ref={rootRef}
					arrowFocusTrapRef={arrowFocusTrapRef}
					{...props}
					engagedRowId={engagedRowId}
					holdRowIndex={keyboardSensorActive ? dragData.index : undefined}
					renderRow={(props) =>
						rowRenderer({
							...props,
							disableDrag,
						})
					}
				/>
				{/** required for virtualized lists */}
				{createPortal(
					<DragOverlay
						dropAnimation={null}
						style={{
							background: theme.palette.background.paper,
							boxShadow: theme.shadows[3],
							border: `1px solid ${theme.palette.primary.main}`,
							marginLeft: dragOverlayLeftMargin,
							overflow: 'hidden',
							width: props.stageWidth,
						}}
						zIndex={9999}
						modifiers={[restrictToVerticalAxis]}
					>
						{dragData.row != null && (
							<TableBodyRowDraggableStyled<TData>
								{...dragData}
								lastSizeableColumnIndex={props.lastSizeableColumnIndex}
								isDragGhost
							/>
						)}
					</DragOverlay>,
					document.body,
				)}
			</SortableContext>
		</DndContext>
	);
};

/**
 * `TableBodyDraggable` is a component that wraps the `TableBody` component and adds drag and drop functionality to the rows
 *
 * NOTE: you must still have a `DragHandleCell` (or equivelant) component to allow the user to actually initiate the drag for rows
 *
 * @typeParam TData The type of data that represents a row
 *
 * @example
 * ```tsx
 * <TableBodyDraggable
 * 	rows={rows}
 * 	columns={columns}
 * 	onRowDragStart={(index) => console.log('drag start', index)}
 * 	onRowDragEnd={(fromIndex, toIndex) => console.log('drag end', fromIndex, toIndex)}
 * />
 * ```
 */
export const TableBodyDraggable = React.memo(
	React.forwardRef(TableBodyDraggablePrimitive),
) as <TData extends RowData>(
	props: TableBodyDraggableProps<TData> & {
		ref?: React.ForwardedRef<HTMLDivElement>;
	},
) => JSX.Element;

export default TableBodyDraggable;
